// /opt/isp_erp/frontend/src/components/warehouse/PendientesSucursalPanel.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, CircularProgress,
  RadioGroup, FormControlLabel, Radio,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "../../api/axios";
import { mensajeError } from "../../utils/mensajesError";

const API = "/warehouse/sucursales-equipos";
const fmtFecha = (v) => (v ? new Date(v).toLocaleDateString("es-EC", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const diasTexto = (d) => (d === 0 ? "Hoy" : d === 1 ? "1 día" : `${d} días`);
const diasColor = (d) => (d > 3 ? "error" : d > 1 ? "warning" : "info");

export function RecibirDialog({ item, onClose, onHecho }) {
  const [sn, setSn] = useState(item && item.sn ? item.sn : "");
  const [modo, setModo] = useState("OPERATIVO");
  const [nota, setNota] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);
  const porSerie = !(item && item.sn);

  const guardar = async () => {
    setError("");
    if (!sn.trim()) { setError("Escribe la serie del equipo"); return; }
    if (modo === "EN_REVISION" && nota.trim().length < 3) { setError("Anota qué falla tiene el equipo"); return; }
    setEnviando(true);
    try {
      const r = await axios.post(`${API}/recibir-de-tecnico`, { sn: sn.trim(), modo, nota: nota.trim() || null });
      onHecho(r.data.mensaje);
    } catch (e) {
      setError(mensajeError(e, "No se pudo registrar la recepción"));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open onClose={enviando ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Recibir en la sucursal</DialogTitle>
      <DialogContent>
        {porSerie ? (
          <TextField fullWidth size="small" label="Serie (SN) del equipo" value={sn} onChange={(e) => setSn(e.target.value)} sx={{ mb: 2 }} autoFocus
            helperText="Para equipos que un técnico de tu sucursal devuelve sin haberlos instalado" />
        ) : (
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{item.sn}</strong> · {item.modelo} · lo devuelve {item.tecnico_nombre}
          </Typography>
        )}
        <Typography variant="subtitle2">¿En qué estado llega el equipo?</Typography>
        <RadioGroup value={modo} onChange={(e) => setModo(e.target.value)}>
          <FormControlLabel value="OPERATIVO" control={<Radio />} label="Operativo: queda disponible en el inventario de la sucursal" />
          <FormControlLabel value="EN_REVISION" control={<Radio />} label="En revisión: tiene una falla por revisar (no se puede asignar mientras tanto)" />
        </RadioGroup>
        <TextField fullWidth size="small" multiline minRows={2} sx={{ mt: 1 }}
          label={modo === "EN_REVISION" ? "Falla que presenta (obligatorio)" : "Observaciones (opcional)"}
          value={nota} onChange={(e) => setNota(e.target.value)} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
        <Button variant="contained" onClick={guardar} disabled={enviando}>{enviando ? "Guardando..." : "Registrar recepción"}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PendientesSucursalPanel({ ctx }) {
  const esCentral = ctx.central !== false;
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [recibiendo, setRecibiendo] = useState(null);

  const puedeOperar = (sid) => {
    const s = (ctx.sucursales || []).find((x) => x.id === sid);
    return !!(s && s.puede_operar);
  };
  const hayOperable = (ctx.sucursales || []).some((s) => s.puede_operar);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError("");
    try {
      const r = await axios.get(`${API}/pendientes`);
      setItems(r.data || []);
    } catch (e) {
      setItems([]);
      setError(mensajeError(e, "No se pudieron cargar los pendientes de las sucursales"));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // En la central, esta sección solo aparece si hay algo pendiente en las sucursales
  if (esCentral && !cargando && !error && items.length === 0 && !aviso) return null;

  return (
    <Box sx={{ p: 3, pb: esCentral ? 0 : 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        <Typography variant={esCentral ? "h5" : "h4"}>
          {esCentral ? "Devoluciones de técnicos de sucursales" : "Equipos Pendientes de Devolución"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {hayOperable && (<Button variant="outlined" onClick={() => setRecibiendo({})}>Recibir por serie</Button>)}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={cargar} disabled={cargando}>Actualizar</Button>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {aviso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso("")}>{aviso}</Alert>}
      {items.some((i) => i.dias_pendiente > 2) && (
        <Alert severity="warning" sx={{ mb: 2 }}>Hay equipos con más de 2 días de retraso en la devolución.</Alert>
      )}
      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell>SN</TableCell>
                  <TableCell>Modelo</TableCell>
                  <TableCell>Técnico</TableCell>
                  {esCentral && <TableCell>Sucursal</TableCell>}
                  <TableCell>Cliente / motivo</TableCell>
                  <TableCell>Fecha de retiro</TableCell>
                  <TableCell>Días</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cargando && (<TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>)}
                {!cargando && items.length === 0 && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>No hay equipos pendientes de devolución en tu sucursal</TableCell></TableRow>
                )}
                {!cargando && items.map((i) => (
                  <TableRow key={i.id} hover>
                    <TableCell><strong>{i.sn}</strong></TableCell>
                    <TableCell>{i.modelo}</TableCell>
                    <TableCell>{i.tecnico_nombre}</TableCell>
                    {esCentral && <TableCell>{i.sucursal_nombre}</TableCell>}
                    <TableCell>
                      {i.cliente || "—"}
                      {i.motivo && (<Typography variant="caption" color="text.secondary" display="block">{i.motivo}</Typography>)}
                    </TableCell>
                    <TableCell>{fmtFecha(i.fecha_retiro)}</TableCell>
                    <TableCell><Chip size="small" color={diasColor(i.dias_pendiente)} label={diasTexto(i.dias_pendiente)} /></TableCell>
                    <TableCell align="center">
                      {puedeOperar(i.sucursal_id) ? (
                        <Button size="small" variant="contained" onClick={() => setRecibiendo(i)}>Recibir en la sucursal</Button>
                      ) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      {recibiendo && (
        <RecibirDialog item={recibiendo} onClose={() => setRecibiendo(null)}
          onHecho={(msg) => { setRecibiendo(null); setAviso(msg); cargar(); }} />
      )}
    </Box>
  );
}
